import React, { useRef, type HTMLAttributes } from 'react';
import cx from 'classnames';
import Card, { CardImage } from '../Card';
import StackLayout from '../StackLayout';
import Text from '../Text';
import stylesheet from './ActionCard.css';

export type ActionCardProps = {
  /** Card image alt text. */
  alt?: string;
  /** Call-to-action text for Card. */
  cta?: React.ReactElement;
  /** Description for Card. */
  description?: string;
  /** Subtitle for card. */
  eyebrow?: string;
  /** Vertical orientation for action card. */
  isVertical?: boolean;
  /** Position placement for image. */
  position?: {
    bottom?: string;
    left?: string;
    right?: string;
    top?: string;
  };
  /** Source for card image. */
  src?: string;
  /** Title for card. */
  title?: string;
  /** A background color matching one of the allowed brand colors. */
  variant?:
    | 'coconut'
    | 'cornflower'
    | 'cucumber'
    | 'radish'
    | 'squash'
    | 'yam'
    | 'jasmine';
  imageOrientation?: 'left' | 'right' | 'top';
} & HTMLAttributes<HTMLDivElement>;

const ActionCard = React.forwardRef<HTMLDivElement, ActionCardProps>(
  function ActionCard(
    {
      alt,
      src,
      title,
      description,
      eyebrow,
      cta,
      position,
      isVertical,
      variant = 'cornflower',
      imageOrientation = 'left',
    },
    forwardedRef,
  ) {
    const linkRef = useRef();

    return (
      <Card
        ref={forwardedRef}
        className={cx(stylesheet.root, {
          [stylesheet[imageOrientation]]:
            imageOrientation && imageOrientation !== 'left',
          [stylesheet.isVertical]: isVertical,
          [stylesheet.noMedia]: !src,
        })}
        appearance="inherit"
      >
        {src && (
          <CardImage
            appearance="basic"
            src={src}
            variant={variant}
            className={stylesheet.imgWrapper}
            position={position}
            alt={alt}
          />
        )}
        <StackLayout className={stylesheet.content}>
          <StackLayout gap={0} className={stylesheet.contentBody}>
            {eyebrow && <Text className={stylesheet.eyebrow}>{eyebrow}</Text>}
            <StackLayout gap={0} className={stylesheet.contentInfo}>
              <Text className={stylesheet.title}>{title}</Text>
              <Text className={stylesheet.description}>{description}</Text>
            </StackLayout>
          </StackLayout>
          {cta &&
            React.cloneElement(cta, {
              ref: linkRef,
              className: cx(
                {
                  [stylesheet.targetButton]: cta?.type === 'button',
                },
                stylesheet.targetLink,
              ),
            })}
        </StackLayout>
      </Card>
    );
  },
);

export default ActionCard;
